import React from "react";
import styled, { keyframes } from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SecurityIcon from "@material-ui/icons/Security";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import LockIcon from "@material-ui/icons/Lock";
import VisibilityIcon from "@material-ui/icons/Visibility";
import StorageIcon from "@material-ui/icons/Storage";
import DeleteIcon from "@material-ui/icons/Delete";
import ShareIcon from "@material-ui/icons/Share";
import ChildCareIcon from "@material-ui/icons/ChildCare";
import UpdateIcon from "@material-ui/icons/Update";
import ContactMailIcon from "@material-ui/icons/ContactMail";
import GetAppIcon from "@material-ui/icons/GetApp";
import GavelIcon from "@material-ui/icons/Gavel";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { CUSTOMER_CARE_NUMBER } from "../constants";
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  SUPPORT_WHATSAPP_DISPLAY,
  OFFICE_ADDRESS,
  COUNTRY_NAME,
  SITE_ORIGIN,
} from "../config/market";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #324D54 0%, #1a2a2e 50%, #0f1a1c 100%);
  padding: var(--lusuku-nav-h, 120px) 20px 140px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: #f8f9fa;
    border-radius: 100% 100% 0 0;
    transform: translateY(50%);
  }

  @media (max-width: 768px) {
    padding: 220px 16px 120px;
    
    &::after {
      height: 50px;
    }
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const HeroIcon = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(255, 193, 7, 0.15);
  border: 2px solid rgba(255, 193, 7, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;

  svg {
    font-size: 40px;
    color: #FFC107;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.2rem;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto 24px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const LastUpdated = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px 20px;
  border-radius: 50px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
`;

const ContentSection = styled.section`
  padding: 60px 20px 80px;
  
  @media (max-width: 768px) {
    padding: 40px 16px 60px;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const QuickNav = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px 32px;
  margin-bottom: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    padding: 20px;
    gap: 8px;
  }
`;

const QuickNavTitle = styled.span`
  font-weight: 700;
  color: #324D54;
  margin-right: 8px;
  font-size: 0.95rem;
`;

const QuickNavLink = styled.a`
  color: #666;
  font-size: 0.85rem;
  text-decoration: none;
  padding: 6px 14px;
  border-radius: 20px;
  background: #f0f0f0;
  transition: all 0.2s ease;

  &:hover {
    background: #324D54;
    color: white;
  }
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #FFC107 0%, #FF9800 100%);
  color: #1a1a1a;
  padding: 14px 28px;
  border-radius: 12px;
  border: none;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  margin-top: 24px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 193, 7, 0.4);
  }

  svg {
    font-size: 20px;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 12px 24px;
  }
`;

const PolicyCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  animation: ${fadeInUp} 0.6s ease-out;
  animation-fill-mode: both;
  animation-delay: ${props => props.$delay || '0s'};

  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 16px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
`;

const SectionIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${props => props.$bg || 'linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)'};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 26px;
    color: white;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const SectionContent = styled.div`
  color: #555;
  font-size: 1rem;
  line-height: 1.8;

  p {
    margin-bottom: 16px;
  }

  strong {
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const SubSection = styled.div`
  margin-top: 24px;
  padding-left: 20px;
  border-left: 3px solid #e0e0e0;
`;

const SubSectionTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: #324D54;
  margin-bottom: 12px;
`;

const DefinitionList = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 16px;
`;

const DefinitionItem = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px 20px;
  border-left: 4px solid ${props => props.$accent || '#324D54'};
`;

const DefinitionTerm = styled.span`
  font-weight: 700;
  color: #1a1a1a;
`;

const DefinitionDesc = styled.span`
  color: #555;
`;

const DataList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0;
  display: grid;
  gap: 12px;
`;

const DataItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  background: #f8f9fa;
  border-radius: 10px;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.6;

  &::before {
    content: '✓';
    color: #10B981;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const RightsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RightItem = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #324D54;
    box-shadow: 0 4px 15px rgba(50, 77, 84, 0.1);
  }
`;

const RightTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const RightDesc = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, #324D54 0%, #1a2a2e 100%);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  color: white;
`;

const ContactIcon = styled.div`
  margin-bottom: 12px;
  
  svg {
    font-size: 2rem;
    color: #FFC107;
  }
`;

const ContactLabel = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 4px;
`;

const ContactValue = styled.div`
  font-size: 1rem;
  font-weight: 600;
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 12px;
  padding: 20px 24px;
  margin: 20px 0;
  display: flex;
  align-items: flex-start;
  gap: 16px;

  svg {
    color: #FFC107;
    font-size: 24px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const HighlightText = styled.p`
  color: #5a4a00;
  font-size: 0.95rem;
  line-height: 1.7;
  margin: 0;
`;

const PolicyPage = () => {
  const handleDownloadPolicy = () => {
    // Create a new window with formatted HTML for PDF printing
    const printWindow = window.open('', '_blank');

    const pdfContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Lusuku Privacy Policy</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #324D54;
          }
          .logo { font-size: 32px; font-weight: 800; color: #324D54; margin-bottom: 8px; }
          .logo span { color: #FFC107; }
          h1 { font-size: 28px; color: #324D54; margin-bottom: 8px; }
          .date { color: #666; font-size: 14px; }
          h2 {
            font-size: 18px;
            color: #324D54;
            margin: 30px 0 15px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e0e0e0;
          }
          p { margin-bottom: 12px; text-align: justify; }
          ul { margin: 12px 0 12px 24px; }
          li { margin-bottom: 8px; }
          .highlight {
            background: #fff8e1;
            border-left: 4px solid #FFC107;
            padding: 12px 16px;
            margin: 16px 0;
            border-radius: 4px;
          }
          .contact-box {
            background: #f5f5f5;
            padding: 20px;
            border-radius: 8px;
            margin-top: 20px;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e0e0e0;
            color: #666;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Lusuku<span>.</span></div>
          <h1>Privacy Policy</h1>
          <p class="date">Last Updated: January 17, 2023</p>
          <p style="color:#888;font-size:12px;margin-top:4px;">Applicable in ${COUNTRY_NAME}</p>
        </div>

        <h2>1. Policy Overview</h2>
        <p>This Privacy Policy describes the policies and procedures of <strong>Lusuku Fresh Services</strong> ("Company", "We", "Us", or "Our") regarding the collection, use, and disclosure of your information when you use our Service. It also informs you about your privacy rights and how the law protects you.</p>
        <p>We use your Personal Data to provide and improve our Service. By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.</p>
        <div class="highlight">
          <strong>Your Consent Matters:</strong> By accessing or using Lusuku's mobile application, website, or any related services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
        </div>

        <h2>2. Definitions</h2>
        <ul>
          <li><strong>Account:</strong> A unique account created for you to access our Service or parts of our Service.</li>
          <li><strong>Application:</strong> Refers to Lusuku mobile and web application and any other software program provided by the Company.</li>
          <li><strong>Company:</strong> Lusuku Fresh Services, SN Plaza, Ntinda Kigoowa, Tuund Road, Kampala, Uganda || ${OFFICE_ADDRESS.join(', ')}.</li>
          <li><strong>Personal Data:</strong> Any information that relates to an identified or identifiable individual.</li>
          <li><strong>Service:</strong> Refers to the Application, Website, and all related services provided by Lusuku.</li>
          <li><strong>Service Provider:</strong> Any natural or legal person who processes data on behalf of the Company.</li>
          <li><strong>Usage Data:</strong> Data collected automatically, generated by the use of the Service.</li>
          <li><strong>You:</strong> The individual accessing or using the Service.</li>
        </ul>

        <h2>3. Data We Collect</h2>
        <p><strong>Personal Data:</strong></p>
        <ul>
          <li>Full name and email address</li>
          <li>Phone number (for order updates and delivery coordination)</li>
          <li>Delivery address, city, and delivery zone</li>
          <li>Payment information (processed securely through our payment partners)</li>
          <li>Order history and preferences</li>
        </ul>
        <p><strong>Usage Data:</strong></p>
        <ul>
          <li>Device's Internet Protocol (IP) address</li>
          <li>Browser type, version, and settings</li>
          <li>Pages visited, time and date of visits, time spent on pages</li>
          <li>Mobile device type, unique device ID, and operating system</li>
          <li>App usage patterns and feature interactions</li>
        </ul>
        <p><strong>Location Data:</strong></p>
        <ul>
          <li>Provide accurate delivery services to your location</li>
          <li>Show nearby restaurants, vendors, and healthcare providers</li>
          <li>Enable real-time order tracking</li>
          <li>Improve our service coverage and delivery efficiency</li>
        </ul>

        <h2>4. How We Use Your Data</h2>
        <ul>
          <li><strong>Service Delivery:</strong> To process and fulfill your orders, coordinate deliveries, and provide customer support</li>
          <li><strong>Account Management:</strong> To manage your registration, maintain your account, and provide access to different functionalities</li>
          <li><strong>Communication:</strong> To send order updates, delivery notifications, and respond to your inquiries</li>
          <li><strong>Personalization:</strong> To provide personalized recommendations based on your preferences and order history</li>
          <li><strong>Analytics:</strong> To analyze usage patterns and improve our Service, features, and user experience</li>
          <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security threats</li>
          <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
          <li><strong>Marketing:</strong> To send promotional offers and updates (with your consent)</li>
        </ul>

        <h2>5. Data Sharing & Disclosure</h2>
        <ul>
          <li><strong>With Vendors:</strong> We share necessary order details with restaurants, retailers, and healthcare providers to fulfill your orders</li>
          <li><strong>With Delivery Partners:</strong> Delivery personnel receive your address and contact information to complete deliveries</li>
          <li><strong>With Payment Processors:</strong> Secure payment partners process your transactions via supported local payment methods</li>
          <li><strong>With Service Providers:</strong> Third-party companies that help us operate, analyze, and improve our Service</li>
          <li><strong>For Legal Reasons:</strong> When required by law, court order, or to protect our rights and safety</li>
          <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of company assets</li>
        </ul>
        <div class="highlight">
          <strong>We Never Sell Your Data:</strong> Lusuku does not sell, rent, or trade your personal information to third parties for their marketing purposes.
        </div>

        <h2>6. Your Privacy Rights</h2>
        <ul>
          <li><strong>Access Your Data:</strong> Request a copy of the personal data we hold about you.</li>
          <li><strong>Correct Your Data:</strong> Update or correct inaccurate personal information in your account.</li>
          <li><strong>Delete Your Data:</strong> Request deletion of your personal data, subject to legal requirements.</li>
          <li><strong>Withdraw Consent:</strong> Opt out of marketing communications at any time.</li>
          <li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format.</li>
          <li><strong>Object to Processing:</strong> Object to certain types of data processing activities.</li>
        </ul>

        <h2>7. Data Security</h2>
        <ul>
          <li>Encryption of data in transit using SSL/TLS protocols</li>
          <li>Secure storage with access controls and authentication</li>
          <li>Regular security assessments and vulnerability testing</li>
          <li>Employee training on data protection and privacy</li>
          <li>Secure payment processing through certified payment partners</li>
        </ul>

        <h2>8. Children's Privacy</h2>
        <p>Our Service is not intended for anyone under the age of 18. We do not knowingly collect personally identifiable information from anyone under 18 years of age. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us.</p>

        <h2>9. Changes to This Policy</h2>
        <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. For significant changes, we will provide a more prominent notice.</p>

        <h2>10. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy, please contact us:</p>
        <div class="contact-box">
          <p><strong>Email:</strong> ${SUPPORT_EMAIL}</p>
          <p><strong>Phone:</strong> ${SUPPORT_PHONE || CUSTOMER_CARE_NUMBER}</p>
          ${SUPPORT_WHATSAPP_DISPLAY ? `<p><strong>WhatsApp:</strong> ${SUPPORT_WHATSAPP_DISPLAY}</p>` : ''}
          <p style="margin-top: 12px;"><strong>Lusuku Fresh Services</strong><br>${OFFICE_ADDRESS.join('<br>')}</p>
        </div>

        <div class="footer">
          <p>&copy; ${new Date().getFullYear()} Lusuku Fresh Services. All rights reserved.</p>
          <p style="margin-top: 8px;">This document was generated from ${SITE_ORIGIN}/policy</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(pdfContent);
    printWindow.document.close();
  };

  return (
    <PageWrapper>
      <Navbar />

      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroIcon>
            <SecurityIcon />
          </HeroIcon>
          <HeroTitle>Privacy Policy</HeroTitle>
          <HeroSubtitle>
            Your privacy is important to us. This policy explains how Lusuku collects,
            uses, and protects your personal information when you use our services.
          </HeroSubtitle>
          <LastUpdated>Last Updated: January 17, 2023</LastUpdated>
          <DownloadButton onClick={handleDownloadPolicy}>
            <GetAppIcon />
            Download Policy
          </DownloadButton>
        </HeroContent>
      </HeroSection>

      {/* Content Section */}
      <ContentSection>
        <Container>
          {/* Quick Navigation */}
          <QuickNav>
            <QuickNavTitle>Jump to:</QuickNavTitle>
            <QuickNavLink href="#overview">Overview</QuickNavLink>
            <QuickNavLink href="#definitions">Definitions</QuickNavLink>
            <QuickNavLink href="#data-collection">Data Collection</QuickNavLink>
            <QuickNavLink href="#data-use">How We Use Data</QuickNavLink>
            <QuickNavLink href="#data-sharing">Data Sharing</QuickNavLink>
            <QuickNavLink href="#your-rights">Your Rights</QuickNavLink>
            <QuickNavLink href="#security">Security</QuickNavLink>
            <QuickNavLink href="#contact">Contact</QuickNavLink>
          </QuickNav>

          {/* Overview */}
          <PolicyCard id="overview" $delay="0.1s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)">
                <VisibilityIcon />
              </SectionIcon>
              <SectionTitle>Policy Overview</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>
                This Privacy Policy describes the policies and procedures of <strong>Lusuku Fresh Services</strong>
                ("Company", "We", "Us", or "Our") regarding the collection, use, and disclosure of your
                information when you use our Service in <strong>{COUNTRY_NAME}</strong>. It also informs you about your privacy rights and
                how the law protects you.
              </p>
              <p>
                We use your Personal Data to provide and improve our Service. By using the Service,
                you agree to the collection and use of information in accordance with this Privacy Policy.
              </p>
              <HighlightBox>
                <GavelIcon />
                <HighlightText>
                  <strong>Your Consent Matters:</strong> By accessing or using Lusuku's mobile application,
                  website, or any related services, you acknowledge that you have read, understood, and
                  agree to be bound by this Privacy Policy.
                </HighlightText>
              </HighlightBox>
            </SectionContent>
          </PolicyCard>

          {/* Definitions */}
          <PolicyCard id="definitions" $delay="0.15s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)">
                <GavelIcon />
              </SectionIcon>
              <SectionTitle>Definitions</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>
                The words with capitalized initial letters have meanings defined under the following
                conditions. These definitions apply whether they appear in singular or plural form.
              </p>
              <DefinitionList>
                <DefinitionItem $accent="#fc5c65">
                  <DefinitionTerm>Account: </DefinitionTerm>
                  <DefinitionDesc>
                    A unique account created for you to access our Service or parts of our Service.
                  </DefinitionDesc>
                </DefinitionItem>
                <DefinitionItem $accent="#324D54">
                  <DefinitionTerm>Application: </DefinitionTerm>
                  <DefinitionDesc>
                    Refers to Lusuku mobile and web application and any other software program provided by the Company.
                  </DefinitionDesc>
                </DefinitionItem>
                <DefinitionItem $accent="#FFC107">
                  <DefinitionTerm>Company: </DefinitionTerm>
                  <DefinitionDesc>
                    Lusuku Fresh Services, located at SN Plaza, Ntinda Kigoowa, Tuund Road, Kampala, Uganda & {OFFICE_ADDRESS.join(', ')}.
                  </DefinitionDesc>
                </DefinitionItem>
                <DefinitionItem $accent="#10B981">
                  <DefinitionTerm>Personal Data: </DefinitionTerm>
                  <DefinitionDesc>
                    Any information that relates to an identified or identifiable individual.
                  </DefinitionDesc>
                </DefinitionItem>
                <DefinitionItem $accent="#6366F1">
                  <DefinitionTerm>Service: </DefinitionTerm>
                  <DefinitionDesc>
                    Refers to the Application, Website, and all related services provided by Lusuku.
                  </DefinitionDesc>
                </DefinitionItem>
                <DefinitionItem $accent="#EC4899">
                  <DefinitionTerm>Service Provider: </DefinitionTerm>
                  <DefinitionDesc>
                    Any natural or legal person who processes data on behalf of the Company, including
                    third-party companies or individuals employed to facilitate, provide, or analyze the Service.
                  </DefinitionDesc>
                </DefinitionItem>
                <DefinitionItem $accent="#14B8A6">
                  <DefinitionTerm>Usage Data: </DefinitionTerm>
                  <DefinitionDesc>
                    Data collected automatically, generated by the use of the Service or from the Service
                    infrastructure itself (e.g., page visit duration, device information).
                  </DefinitionDesc>
                </DefinitionItem>
                <DefinitionItem $accent="#F59E0B">
                  <DefinitionTerm>You: </DefinitionTerm>
                  <DefinitionDesc>
                    The individual accessing or using the Service, or the company or legal entity on
                    whose behalf such individual is accessing the Service.
                  </DefinitionDesc>
                </DefinitionItem>
              </DefinitionList>
            </SectionContent>
          </PolicyCard>

          {/* Data Collection */}
          <PolicyCard id="data-collection" $delay="0.2s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #10B981 0%, #059669 100%)">
                <StorageIcon />
              </SectionIcon>
              <SectionTitle>Data We Collect</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <SubSection>
                <SubSectionTitle>Personal Data</SubSectionTitle>
                <p>
                  While using our Service, we may ask you to provide certain personally identifiable
                  information that can be used to contact or identify you:
                </p>
                <DataList>
                  <DataItem>Full name and email address</DataItem>
                  <DataItem>Phone number (for order updates and delivery coordination)</DataItem>
                  <DataItem>Delivery address, city, and delivery zone</DataItem>
                  <DataItem>Payment information (processed securely through our payment partners)</DataItem>
                  <DataItem>Order history and preferences</DataItem>
                </DataList>
              </SubSection>

              <SubSection>
                <SubSectionTitle>Usage Data</SubSectionTitle>
                <p>
                  Usage Data is collected automatically when using the Service and may include:
                </p>
                <DataList>
                  <DataItem>Device's Internet Protocol (IP) address</DataItem>
                  <DataItem>Browser type, version, and settings</DataItem>
                  <DataItem>Pages visited, time and date of visits, time spent on pages</DataItem>
                  <DataItem>Mobile device type, unique device ID, and operating system</DataItem>
                  <DataItem>App usage patterns and feature interactions</DataItem>
                </DataList>
              </SubSection>

              <SubSection>
                <SubSectionTitle>Location Data</SubSectionTitle>
                <p>
                  With your prior permission, we collect location information to:
                </p>
                <DataList>
                  <DataItem>Provide accurate delivery services to your location</DataItem>
                  <DataItem>Show nearby restaurants, vendors, and healthcare providers</DataItem>
                  <DataItem>Enable real-time order tracking</DataItem>
                  <DataItem>Improve our service coverage and delivery efficiency</DataItem>
                </DataList>
                <HighlightBox>
                  <LockIcon />
                  <HighlightText>
                    You can enable or disable location access at any time through your device settings.
                    Disabling location may limit certain features of the Service.
                  </HighlightText>
                </HighlightBox>
              </SubSection>
            </SectionContent>
          </PolicyCard>

          {/* How We Use Data */}
          <PolicyCard id="data-use" $delay="0.25s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)">
                <VerifiedUserIcon />
              </SectionIcon>
              <SectionTitle>How We Use Your Data</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>Lusuku uses your collected data for the following purposes:</p>
              <DataList>
                <DataItem><strong>Service Delivery:</strong> To process and fulfill your orders, coordinate deliveries, and provide customer support</DataItem>
                <DataItem><strong>Account Management:</strong> To manage your registration, maintain your account, and provide access to different functionalities</DataItem>
                <DataItem><strong>Communication:</strong> To send order updates, delivery notifications, and respond to your inquiries</DataItem>
                <DataItem><strong>Personalization:</strong> To provide personalized recommendations based on your preferences and order history</DataItem>
                <DataItem><strong>Analytics:</strong> To analyze usage patterns and improve our Service, features, and user experience</DataItem>
                <DataItem><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security threats</DataItem>
                <DataItem><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</DataItem>
                <DataItem><strong>Marketing:</strong> To send promotional offers and updates (with your consent, which you can withdraw anytime)</DataItem>
              </DataList>
            </SectionContent>
          </PolicyCard>

          {/* Data Sharing */}
          <PolicyCard id="data-sharing" $delay="0.3s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #EC4899 0%, #DB2777 100%)">
                <ShareIcon />
              </SectionIcon>
              <SectionTitle>Data Sharing & Disclosure</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>We may share your personal information in the following situations:</p>
              <DataList>
                <DataItem><strong>With Vendors:</strong> We share necessary order details with restaurants, retailers, and healthcare providers to fulfill your orders</DataItem>
                <DataItem><strong>With Delivery Partners:</strong> Delivery personnel receive your address and contact information to complete deliveries</DataItem>
                <DataItem><strong>With Payment Processors:</strong> Secure payment partners process your transactions via supported local payment methods in {COUNTRY_NAME}</DataItem>
                <DataItem><strong>With Service Providers:</strong> Third-party companies that help us operate, analyze, and improve our Service</DataItem>
                <DataItem><strong>For Legal Reasons:</strong> When required by law, court order, or to protect our rights and safety</DataItem>
                <DataItem><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of company assets</DataItem>
              </DataList>
              <HighlightBox>
                <SecurityIcon />
                <HighlightText>
                  <strong>We Never Sell Your Data:</strong> Lusuku does not sell, rent, or trade your personal
                  information to third parties for their marketing purposes.
                </HighlightText>
              </HighlightBox>
            </SectionContent>
          </PolicyCard>

          {/* Data Retention */}
          <PolicyCard id="retention" $delay="0.35s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)">
                <DeleteIcon />
              </SectionIcon>
              <SectionTitle>Data Retention & Deletion</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>
                We retain your Personal Data only for as long as necessary for the purposes set out
                in this Privacy Policy. We will retain and use your data to the extent necessary to:
              </p>
              <DataList>
                <DataItem>Comply with our legal obligations (e.g., tax and accounting requirements)</DataItem>
                <DataItem>Resolve disputes and enforce our agreements</DataItem>
                <DataItem>Maintain business records as required by {COUNTRY_NAME} law</DataItem>
              </DataList>
              <p>
                Usage Data is generally retained for a shorter period, except when used to strengthen
                security, improve functionality, or when we are legally obligated to retain it longer.
              </p>
            </SectionContent>
          </PolicyCard>

          {/* Your Rights */}
          <PolicyCard id="your-rights" $delay="0.4s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)">
                <VerifiedUserIcon />
              </SectionIcon>
              <SectionTitle>Your Privacy Rights</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>
                You have certain rights regarding your personal data. Here's what you can do:
              </p>
              <RightsList>
                <RightItem>
                  <RightTitle>Access Your Data</RightTitle>
                  <RightDesc>Request a copy of the personal data we hold about you.</RightDesc>
                </RightItem>
                <RightItem>
                  <RightTitle>Correct Your Data</RightTitle>
                  <RightDesc>Update or correct inaccurate personal information in your account.</RightDesc>
                </RightItem>
                <RightItem>
                  <RightTitle>Delete Your Data</RightTitle>
                  <RightDesc>Request deletion of your personal data, subject to legal requirements.</RightDesc>
                </RightItem>
                <RightItem>
                  <RightTitle>Withdraw Consent</RightTitle>
                  <RightDesc>Opt out of marketing communications at any time.</RightDesc>
                </RightItem>
                <RightItem>
                  <RightTitle>Data Portability</RightTitle>
                  <RightDesc>Request your data in a structured, machine-readable format.</RightDesc>
                </RightItem>
                <RightItem>
                  <RightTitle>Object to Processing</RightTitle>
                  <RightDesc>Object to certain types of data processing activities.</RightDesc>
                </RightItem>
              </RightsList>
              <p style={{ marginTop: '20px' }}>
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </SectionContent>
          </PolicyCard>

          {/* Security */}
          <PolicyCard id="security" $delay="0.45s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)">
                <LockIcon />
              </SectionIcon>
              <SectionTitle>Data Security</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>
                The security of your Personal Data is important to us. We implement appropriate
                technical and organizational measures to protect your information, including:
              </p>
              <DataList>
                <DataItem>Encryption of data in transit using SSL/TLS protocols</DataItem>
                <DataItem>Secure storage with access controls and authentication</DataItem>
                <DataItem>Regular security assessments and vulnerability testing</DataItem>
                <DataItem>Employee training on data protection and privacy</DataItem>
                <DataItem>Secure payment processing through certified payment partners</DataItem>
              </DataList>
              <HighlightBox>
                <SecurityIcon />
                <HighlightText>
                  While we strive to use commercially acceptable means to protect your Personal Data,
                  no method of transmission over the Internet or electronic storage is 100% secure.
                  We cannot guarantee absolute security but are committed to protecting your information.
                </HighlightText>
              </HighlightBox>
            </SectionContent>
          </PolicyCard>

          {/* Children's Privacy */}
          <PolicyCard $delay="0.5s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #F472B6 0%, #EC4899 100%)">
                <ChildCareIcon />
              </SectionIcon>
              <SectionTitle>Children's Privacy</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>
                Our Service is not intended for anyone under the age of 18. We do not knowingly
                collect personally identifiable information from anyone under 18 years of age.
              </p>
              <p>
                If you are a parent or guardian and you are aware that your child has provided us
                with Personal Data, please contact us. If we become aware that we have collected
                Personal Data from anyone under 18 without verification of parental consent, we
                will take steps to remove that information from our servers.
              </p>
            </SectionContent>
          </PolicyCard>

          {/* Policy Updates */}
          <PolicyCard $delay="0.55s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)">
                <UpdateIcon />
              </SectionIcon>
              <SectionTitle>Changes to This Policy</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page and updating the
                "Last Updated" date at the top.
              </p>
              <p>
                For significant changes, we will provide a more prominent notice, which may include
                an email notification or an in-app announcement. We encourage you to review this
                Privacy Policy periodically for any changes.
              </p>
            </SectionContent>
          </PolicyCard>

          {/* Contact */}
          <PolicyCard id="contact" $delay="0.6s">
            <SectionHeader>
              <SectionIcon $bg="linear-gradient(135deg, #10B981 0%, #059669 100%)">
                <ContactMailIcon />
              </SectionIcon>
              <SectionTitle>Contact Us</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <p>
                If you have any questions about this Privacy Policy, your personal data, or would
                like to exercise your privacy rights, please contact us:
              </p>
              <ContactGrid>
                <ContactCard>
                  <ContactIcon><EmailIcon /></ContactIcon>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue>{SUPPORT_EMAIL}</ContactValue>
                </ContactCard>
                <ContactCard>
                  <ContactIcon><PhoneIcon /></ContactIcon>
                  <ContactLabel>Phone</ContactLabel>
                  <ContactValue>{SUPPORT_PHONE || CUSTOMER_CARE_NUMBER}</ContactValue>
                </ContactCard>
                <ContactCard>
                  <ContactIcon><WhatsAppIcon /></ContactIcon>
                  <ContactLabel>WhatsApp</ContactLabel>
                  <ContactValue>{SUPPORT_WHATSAPP_DISPLAY || SUPPORT_PHONE || CUSTOMER_CARE_NUMBER}</ContactValue>
                </ContactCard>
              </ContactGrid>
              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <p style={{ color: '#666', fontSize: '0.95rem' }}>
                  <strong>Lusuku Fresh Services</strong><br />
                  {OFFICE_ADDRESS.map((line, i) => (
                    <span key={i}>{line}{i < OFFICE_ADDRESS.length - 1 ? <br /> : null}</span>
                  ))}
                </p>
              </div>
            </SectionContent>
          </PolicyCard>

        </Container>
      </ContentSection>

      <Footer />
    </PageWrapper>
  );
};

export default PolicyPage;
